import {
  CitiesWithHotels,
  createHotel,
  duplicateHotel,
  getChains,
  getHotel,
  getHotelCities,
  updateHotel,
} from '@/api/hotel.api';
import { invalidateBenefitLists } from '@/hooks/useBenefits';
import { queryKeys } from '@/queryKeys';
import { SupportedLocale } from '@/types/supported-locale.type';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useCreateHotel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: FormData) => createHotel(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.hotels.all });
      invalidateBenefitLists(queryClient);
    },
  });
};

export const useUpdateHotel = (language?: SupportedLocale) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ benefitId, data }: { benefitId: string; data: FormData }) =>
      updateHotel(benefitId, data, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.hotels.all });
      invalidateBenefitLists(queryClient);
    },
  });
};

export const useGetHotelCities = <TData = CitiesWithHotels>({
  country,
  select,
}: {
  country?: string;
  select?: (data: CitiesWithHotels) => TData;
}) =>
  useQuery({
    queryKey: queryKeys.hotels.cities(country),
    queryFn: () => getHotelCities(country),
    select,
  });

export const useGetChains = <TData = { chain: string }[]>(
  select?: (data: { chain: string }[]) => TData,
) =>
  useQuery({
    queryKey: queryKeys.hotels.chains,
    queryFn: () => getChains(),
    select,
  });

export const useGetHotel = (benefitId: string) =>
  useQuery({
    queryKey: queryKeys.hotels.detail(benefitId),
    queryFn: () => getHotel(benefitId),
  });

export const useDuplicateHotel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      benefitId,
      fields,
    }: {
      benefitId: string;
      fields: string[];
    }) => duplicateHotel(benefitId, fields),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.hotels.all });
      invalidateBenefitLists(queryClient);
    },
  });
};
